import {
  type TextareaHTMLAttributes,
  type Ref,
  forwardRef,
  useState,
  type FocusEvent,
  type AnimationEvent,
  type ChangeEvent,
  useEffect,
  useRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import styled from 'styled-components';

import Layout from '@yoweb/ui/components/form/Layout';
import { th } from '@yoweb/ui/styles/utils/theme';
import { formInputCommon } from '@yoweb/ui/components/form/formStyles';
import type { InputProps } from '@yoweb/ui/components/form/Input/InputUI';
import { isElevatedLabel } from '@yoweb/ui/components/form/formUtils';
import { isString } from '@yoweb/utils/isString';
import { pxToRem } from '@yoweb/ui/styles/utils';
import { type MaxHeight, maxHeightMixin } from '@yoweb/ui/styles/utils/mixins.utils';
import { textBase } from '@yoweb/ui/components/typography';
import { useTranslation } from '@yoweb/next-i18next';

const onAutoFillStart = 'onAutoFillStart';
const onAutoFillCancel = 'onAutoFillCancel';
const TEXT_AREA_LINE_HEIGHT = 24;
const MAX_ROWS = 10;

const computeRows = (scrollHeight: number) =>
  Math.min(Math.floor((scrollHeight - TEXT_AREA_LINE_HEIGHT) / TEXT_AREA_LINE_HEIGHT), MAX_ROWS);

export type TextareaProps = {
  rows?: number;
  maxHeight?: number;
  maxLengthOverflow?: boolean;
  /* Overrides the default maxLength error message. Cannot rely on Formik's meta.error because we only pass it on blur. */
  maxLengthErrorMessage?: string;
} & InputProps &
  MaxHeight &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'required'>;

const CHARACTER_COUNT_REMAINDER_LIMIT = 5;

const useTextareaCounter = (
  currentValueString: string,
  currentError: string | undefined,
  maxLength: number,
  maxLengthErrorMessage?: string,
) => {
  const { t } = useTranslation<'common'>();

  return useMemo(() => {
    const warningErrorState =
      currentValueString.length >= maxLength
        ? maxLengthErrorMessage ?? t('validation.max-word-count')
        : undefined;

    const suffixLabel =
      maxLength - currentValueString.length <= CHARACTER_COUNT_REMAINDER_LIMIT
        ? `${currentValueString.length} / ${maxLength}`
        : undefined;

    return {
      // any existing errors take priority over our max length *warning* (not a true error)
      error: currentError ?? warningErrorState,
      suffixLabel,
    };
  }, [currentError, currentValueString.length, maxLength, maxLengthErrorMessage, t]);
};

const Textarea = (
  {
    maxLength = 2_000,
    rows = 4,
    maxHeight,
    maxLengthOverflow,
    maxLengthErrorMessage,
    ...props
  }: TextareaProps,
  ref: Ref<HTMLTextAreaElement>,
) => {
  const currentValue = props.defaultValue ?? props.value;
  const [hasFocus, setFocus] = useState(false);
  const [hasAutoFill, setHasAutofill] = useState(false);
  const isStringValue = isString(props.value, true);
  const [displayRows, setDisplayRows] = useState(() => rows);
  const innerRef = useRef<HTMLTextAreaElement>(null);
  const hasValue = Boolean(hasAutoFill || isStringValue ? isString(props.value) : currentValue);
  const { error, suffixLabel } = useTextareaCounter(
    typeof currentValue === 'string' ? currentValue : '',
    props.error,
    maxLength,
    maxLengthErrorMessage,
  );

  useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

  useEffect(() => {
    if (innerRef.current) {
      const currentRows = computeRows(innerRef.current.scrollHeight);
      setDisplayRows(currentRows);
    }
  }, [setDisplayRows]);

  const elevatedLabel = isElevatedLabel({
    placeholder: props.placeholder,
    label: props.label,
    hasFocus,
    hasValue,
  });

  const onFocusHandler = (event: FocusEvent<HTMLTextAreaElement>) => {
    setFocus(true);
    props.onFocus?.(event);
  };

  const onBlurHandler = (event: FocusEvent<HTMLTextAreaElement>) => {
    setFocus(false);
    props.onBlur?.(event);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
    props.onChange?.(event);
    event.target.rows = rows;
    const currentRows = computeRows(event.target.scrollHeight);
    event.target.rows = currentRows;
    setDisplayRows(currentRows);
  };

  /**
   * When animation occurs on the input element we fire onAnimationStart event
   * We can detect the css animation name on the event and then determine if an browser
   * autofill has occurred.
   */
  const onAnimationStart = (e: AnimationEvent<HTMLTextAreaElement>) => {
    if (e.animationName === onAutoFillStart) {
      setHasAutofill(true);
    }
  };

  return (
    <Layout
      isFocused={hasFocus}
      hasValue={hasValue}
      suffixLabel={suffixLabel}
      {...props}
      error={error}
    >
      <StyledTextarea
        {...props}
        rows={displayRows}
        placeholder={props.placeholder}
        elevatedLabel={elevatedLabel}
        hasError={!!error}
        hasIcon={!!props.icon}
        maxLength={maxLengthOverflow ? undefined : maxLength}
        onAnimationStart={onAnimationStart}
        onBlur={onBlurHandler}
        onFocus={onFocusHandler}
        onChange={onChangeHandler}
        maxHeight={maxHeight}
        ref={innerRef}
      />
      {elevatedLabel && <LabelBackground />}
    </Layout>
  );
};

type StyledTextareaProps = {
  hasError: boolean;
  hasIcon?: boolean;
  elevatedLabel?: boolean;
} & MaxHeight;

const LabelBackground = styled.span`
  background: ${th.getColor('base000')};
  border-radius: ${th.getRadii('small')};
  height: ${pxToRem(28)};
  left: 0;
  opacity: 0.9;
  position: absolute;
  right: ${th.getSpace('normal1')};
  top: ${pxToRem(2)};
  z-index: 0;
`;

const StyledTextarea = styled.textarea<StyledTextareaProps>`
  ${textBase};

  padding-left: ${th.getSpace('normal1')};
  padding-right: ${({ hasIcon }) => (hasIcon ? th.getSpace('medium1') : th.getSpace('normal1'))};
  resize: none;

  /**
  * This autofill hack helps us detect animation name on event onAnimationStart
  */
  /* stylelint-disable block-no-empty */
  @keyframes onAutoFillStart {
  }

  @keyframes onAutoFillCancel {
  }
  /* stylelint-enable block-no-empty */

  :-webkit-autofill {
    animation-name: ${onAutoFillStart};
  }

  &:not(:-webkit-autofill) {
    animation-name: ${onAutoFillCancel};
  }

  ${formInputCommon};
  ${maxHeightMixin};

  overflow-y: auto;

  &:hover,
  &:disabled {
    & + ${LabelBackground} {
      background: ${th.getColor('base050')};
    }
  }
`;

export default forwardRef(Textarea);
